import React, { useState, useRef, useEffect } from "react";
import { Filter, Check, X } from "lucide-react";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { FreshDot } from "@/components/LiveIndicator";

const FIRST_RUN_KEY = "rl_seen_status_filter_hint";
// Module-level flag so only ONE popover instance shows the coach-mark text
// bubble (otherwise three identical bubbles would stack on a stage page).
let bubbleClaimedBy = null;

/**
 * StatusFilterPopover — column-header micro-filter.
 *
 * Renders a small Filter icon button next to a column title. Clicking it
 * opens a floating checkbox list of statuses. Selecting one or more values
 * limits the table to rows matching those statuses; clearing all (or hitting
 * the "All" button) restores the full set.
 *
 * Props:
 *   columnLabel    string — header text rendered before the icon
 *   tooltip        string — tooltip on the filter trigger
 *   statuses       string[] — possible status values
 *   selected       string[] — currently active filter values (empty == all)
 *   onChange       (next: string[]) => void
 *   accentColor    string — CSS color used for the active-state ring
 *   testIdPrefix   string — used to build data-testid for trigger + items
 *   metaFilters    optional [{key, label, active, onToggle, hint?}] — synthetic
 *                  toggle filters (e.g. "Route Card Pending") rendered below
 *                  the status checkboxes. Active state is independent and adds
 *                  to the popover's badge count.
 */
export default function StatusFilterPopover({
  columnLabel, tooltip, statuses, selected, onChange, accentColor = "#f97316", testIdPrefix,
  metaFilters = [],
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // First-time discovery coach-mark: shown until the user opens any filter
  // popover or dismisses it. Once gone, it never returns (per-browser).
  const [showHint, setShowHint] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return !window.localStorage.getItem(FIRST_RUN_KEY); } catch { return false; }
  });
  const dismissHint = React.useCallback(() => {
    setShowHint(false);
    bubbleClaimedBy = null;
    try { window.localStorage.setItem(FIRST_RUN_KEY, "1"); } catch { /* ignore */ }
  }, []);
  // Claim the right to render the floating bubble (only first mounted popover wins).
  const instanceIdRef = useRef(Symbol("filter-popover"));
  const [ownsBubble, setOwnsBubble] = useState(false);
  useEffect(() => {
    if (!showHint) return;
    if (bubbleClaimedBy === null) {
      bubbleClaimedBy = instanceIdRef.current;
      setOwnsBubble(true);
    }
    return () => {
      if (bubbleClaimedBy === instanceIdRef.current) {
        bubbleClaimedBy = null;
      }
    };
  }, [showHint]);
  // Cross-tab + cross-instance sync — when one popover dismisses the hint,
  // every other rendered StatusFilterPopover hides too.
  useEffect(() => {
    if (!showHint) return;
    const onStorage = (e) => { if (e.key === FIRST_RUN_KEY && e.newValue) setShowHint(false); };
    window.addEventListener("storage", onStorage);
    const onCustom = () => setShowHint(false);
    window.addEventListener("rl-status-filter-hint-dismissed", onCustom);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("rl-status-filter-hint-dismissed", onCustom);
    };
  }, [showHint]);

  // Outside click / ESC handled automatically by Radix Popover

  const sel = new Set(selected || []);
  const metaActiveCount = metaFilters.filter(m => m.active).length;
  const active = sel.size > 0 || metaActiveCount > 0;
  const totalActive = sel.size + metaActiveCount;

  const toggle = (s) => {
    const next = new Set(sel);
    if (next.has(s)) next.delete(s); else next.add(s);
    onChange(Array.from(next));
  };
  const clearAll = () => {
    onChange([]);
    metaFilters.forEach(m => { if (m.active) m.onToggle(); });
  };

  return (
    <span className="inline-flex items-center gap-1.5 relative" ref={ref}>
      <span>{columnLabel}</span>
      <Popover open={open} onOpenChange={(o) => {
        setOpen(o);
        if (o && showHint) {
          dismissHint();
          try { window.dispatchEvent(new Event("rl-status-filter-hint-dismissed")); } catch { /* ignore */ }
        }
      }}>
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={`relative inline-flex items-center justify-center w-7 h-7 -my-1.5 rounded-full rl-well transition-colors normal-case ${
                    active
                      ? "text-white"
                      : "text-[hsl(var(--muted-foreground))]/70 hover:text-[hsl(var(--foreground))]"
                  }`}
                  style={active ? { background: accentColor } : {}}
                  aria-haspopup="dialog"
                  aria-expanded={open}
                  data-testid={`${testIdPrefix}-trigger`}
                >
                  <Filter className="w-3 h-3" />
                  {showHint && !active && (
                    <FreshDot
                      color={accentColor}
                      label=""
                      className="absolute -top-0.5 -right-0.5"
                    />
                  )}
                </button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-[10px]">{tooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* First-time coach-mark — rendered OUTSIDE the Popover so it doesn't
            depend on popover open-state. Only first mounted instance claims it. */}
        {showHint && ownsBubble && !active && (
          <span
            className="absolute z-30 normal-case animate-in fade-in slide-in-from-left-1 duration-300"
            style={{ transform: "translateY(-50%)", marginLeft: "28px", left: "100%", top: "50%" }}
            role="status"
            data-testid="status-filter-coach-mark"
          >
            <span className="relative inline-flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-md text-[11px] font-medium text-white shadow-md whitespace-nowrap"
                 style={{ background: accentColor }}>
              <span
                aria-hidden="true"
                className="absolute right-full top-1/2 -translate-y-1/2 w-0 h-0"
                style={{
                  borderTop: "5px solid transparent",
                  borderBottom: "5px solid transparent",
                  borderRight: `5px solid ${accentColor}`,
                }}
              />
              <span>New — filter by status</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  dismissHint();
                  try { window.dispatchEvent(new Event("rl-status-filter-hint-dismissed")); } catch { /* ignore */ }
                }}
                className="inline-flex items-center justify-center w-4 h-4 rounded-sm hover:bg-white/20 transition-colors"
                aria-label="Dismiss tip"
                data-testid="status-filter-coach-dismiss"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          </span>
        )}

        {active && (
          <span
            className="text-[9px] font-mono-data tabular-nums px-1 rounded normal-case"
            style={{ color: accentColor, background: accentColor + "15", border: `1px solid ${accentColor}40` }}
            data-testid={`${testIdPrefix}-count`}
          >
            {totalActive}
          </span>
        )}

        <PopoverContent
          align="start"
          sideOffset={6}
          collisionPadding={12}
          className="z-50 p-0 w-[210px] rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-lg overflow-hidden normal-case"
          data-testid={`${testIdPrefix}-popover`}
        >
          <div className="px-3 py-2 flex items-center justify-between gap-2 border-b border-[hsl(var(--border))]/60 bg-[hsl(var(--muted))]/40">
            <span className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">Filter status</span>
            {active && (
              <button
                type="button"
                onClick={clearAll}
                className="text-[9px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] inline-flex items-center gap-0.5 normal-case"
                data-testid={`${testIdPrefix}-clear`}
              >
                <X className="w-2.5 h-2.5" /> Clear
              </button>
            )}
          </div>
          <ul className="max-h-[260px] overflow-y-auto py-1 normal-case">
            {statuses.map(s => {
              const checked = sel.has(s);
              return (
                <li key={s}>
                  <button
                    type="button"
                    onClick={() => toggle(s)}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-[12px] hover:bg-[hsl(var(--muted))]/40 transition-colors ${checked ? "font-semibold" : "font-normal"}`}
                    data-testid={`${testIdPrefix}-opt-${s.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <span
                      className="w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-colors"
                      style={checked
                        ? { background: accentColor, borderColor: accentColor }
                        : { borderColor: "hsl(var(--border))" }}
                    >
                      {checked && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                    </span>
                    <span className="text-left flex-1 text-[hsl(var(--foreground))]">{s}</span>
                  </button>
                </li>
              );
            })}
            {metaFilters.length > 0 && (
              <>
                <li
                  className="px-3 pt-2 pb-1 text-[9px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]/70 border-t border-[hsl(var(--border))]/50 mt-1"
                  aria-hidden="true"
                >
                  Gates
                </li>
                {metaFilters.map((m) => (
                  <li key={m.key}>
                    <button
                      type="button"
                      onClick={m.onToggle}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 text-[12px] hover:bg-[hsl(var(--muted))]/40 transition-colors ${m.active ? "font-semibold" : "font-normal"}`}
                      data-testid={`${testIdPrefix}-meta-${m.key}`}
                      title={m.hint}
                    >
                      <span
                        className="w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-colors"
                        style={m.active
                          ? { background: accentColor, borderColor: accentColor }
                          : { borderColor: "hsl(var(--border))" }}
                      >
                        {m.active && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                      </span>
                      <span className="text-left flex-1 text-[hsl(var(--foreground))]">{m.label}</span>
                    </button>
                  </li>
                ))}
              </>
            )}
          </ul>
        </PopoverContent>
      </Popover>
    </span>
  );
}
